import type { ClientMessage, ServerMessage } from "../shared/types";

export type ConnectionStatus = "connecting" | "connected" | "reconnecting" | "offline";

type SocketOptions = {
  boardId: string;
  mode: "overlay" | "control";
  token?: string;
  onMessage: (message: ServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
};

export class BingoSocket {
  private readonly options: SocketOptions;
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private heartbeatTimer: number | null = null;
  private reconnectAttempt = 0;
  private stopped = false;
  private everSynced = false;
  private writeAuthenticated = false;

  constructor(options: SocketOptions) {
    this.options = options;
  }

  get canWrite(): boolean {
    return this.socket?.readyState === WebSocket.OPEN && this.writeAuthenticated;
  }

  start(): void {
    this.stopped = false;
    this.connect();
    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);
    document.addEventListener("visibilitychange", this.handleVisibility);
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
    document.removeEventListener("visibilitychange", this.handleVisibility);
    this.clearTimers();
    this.socket?.close(1000, "Pagina gesloten");
    this.socket = null;
  }

  send(message: ClientMessage): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    try {
      this.socket.send(JSON.stringify(message));
      return true;
    } catch {
      this.reconnectNow();
      return false;
    }
  }

  private connect(): void {
    if (this.stopped || this.socket?.readyState === WebSocket.CONNECTING || this.socket?.readyState === WebSocket.OPEN) {
      return;
    }
    if (!navigator.onLine) {
      this.options.onStatus("offline");
      return;
    }

    this.options.onStatus(this.reconnectAttempt > 0 ? "reconnecting" : "connecting");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const url = new URL(`${protocol}//${location.host}/api/boards/${this.options.boardId}/ws`);
    url.searchParams.set("mode", this.options.mode);
    const socket = new WebSocket(url);
    this.socket = socket;
    this.writeAuthenticated = false;

    socket.addEventListener("open", () => {
      this.reconnectAttempt = 0;
      this.options.onStatus("connected");
      this.startHeartbeat();
      if (this.options.mode === "control" && this.options.token) {
        this.send({ type: "authenticate", token: this.options.token });
      } else {
        this.requestState();
      }
    });

    socket.addEventListener("message", (event: MessageEvent<unknown>) => {
      if (typeof event.data !== "string") return;
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === "authenticated") {
        this.writeAuthenticated = true;
        this.requestState();
      }
      if (message.type === "state") this.everSynced = true;
      this.options.onMessage(message);
    });

    socket.addEventListener("close", () => {
      if (this.socket === socket) this.socket = null;
      this.writeAuthenticated = false;
      this.stopHeartbeat();
      if (!this.stopped) this.scheduleReconnect();
    });

    socket.addEventListener("error", () => {
      socket.close();
    });
  }

  private requestState(): void {
    this.send({ type: "request_state", reason: this.everSynced ? "reconnect" : "initial" });
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    if (!navigator.onLine) {
      this.options.onStatus("offline");
      return;
    }
    this.options.onStatus("reconnecting");
    const base = Math.min(15_000, 500 * 2 ** Math.min(this.reconnectAttempt, 5));
    const delay = base + Math.floor(Math.random() * Math.min(1_000, base / 2));
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private reconnectNow(): void {
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
    this.scheduleReconnect();
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = window.setInterval(() => this.send({ type: "ping" }), 20_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) window.clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private clearTimers(): void {
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.stopHeartbeat();
  }

  private readonly handleOnline = (): void => this.reconnectNow();

  private readonly handleOffline = (): void => {
    this.options.onStatus("offline");
    this.socket?.close();
  };

  private readonly handleVisibility = (): void => {
    if (document.visibilityState !== "visible") return;
    if (this.socket?.readyState === WebSocket.OPEN) this.requestState();
    else this.reconnectNow();
  };
}
